import { articles, type Article } from "@/data/articles";

export type PageEntry = {
  title: string;
  description: string;
  url: string;
};

export const PAGES: PageEntry[] = [
  { title: "Home", description: "Taekwondo Mattei Roma — scuola storica del quadrante Portuense/Monteverde.", url: "/" },
  { title: "Prenota una lezione di prova", description: "Prima lezione gratuita e senza impegno per bambini, ragazzi e adulti.", url: "/lezione-di-prova" },
  { title: "Iscrizioni Stagione 2026/2027", description: "Iscriviti alla nuova stagione: corsi per agonisti e non, tutti i livelli.", url: "/stagione-2026-2027" },
  { title: "A.S.D. Taekwondo Mattei", description: "La nostra società sportiva: storia, valori e organizzazione.", url: "/asd" },
  { title: "Academy", description: "Il metodo Mattei: formiamo prima persone, non campioni.", url: "/academy" },
  { title: "Percorsi", description: "I percorsi formativi: bambini, ragazzi, adulti e agonismo.", url: "/percorsi" },
  { title: "Maestri", description: "I Maestri del Taekwondo Mattei Roma.", url: "/maestri" },
  { title: "Vita in Palestra", description: "Foto, video ed eventi della nostra palestra a Roma.", url: "/vita-in-palestra" },
  { title: "Blog", description: "Articoli, guide e approfondimenti sul Taekwondo a Roma.", url: "/blog" },
  { title: "Contatti", description: "Dove siamo, come raggiungerci e come contattarci.", url: "/contatti" },
  { title: "Costi del Taekwondo a Roma", description: "Guida completa ai prezzi: iscrizione, quote mensili, dobok, esami.", url: "/blog/costi-taekwondo-roma" },
];

export const SUGGESTED_URLS: string[] = [
  "/lezione-di-prova",
  "/stagione-2026-2027",
  "/percorsi",
  "/asd",
  "/maestri",
  "/academy",
  "/blog/costi-taekwondo-roma",
  "/contatti",
];

export const SUGGESTED_PAGES: PageEntry[] = SUGGESTED_URLS
  .map((u) => PAGES.find((p) => p.url === u))
  .filter((p): p is PageEntry => Boolean(p));

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const scoreMatch = (haystack: string, tokens: string[], weight: number): number => {
  const norm = normalize(haystack);
  let score = 0;
  for (const t of tokens) {
    if (!t) continue;
    if (norm.includes(t)) score += weight;
  }
  return score;
};

export type SearchResults = {
  pages: PageEntry[];
  articles: Article[];
};

export function searchAll(query: string, limit = 8): SearchResults {
  const q = normalize(query.trim());
  if (!q) return { pages: [], articles: [] };
  const tokens = q.split(/\s+/).filter(Boolean);

  // Pages: match all tokens against title+description (AND)
  const scoredPages = PAGES.map((p) => {
    const titleScore = scoreMatch(p.title, tokens, 3);
    const descScore = scoreMatch(p.description, tokens, 1);
    const urlScore = scoreMatch(p.url, tokens, 1);
    const total = titleScore + descScore + urlScore;
    // require every token to match somewhere
    const combined = normalize(`${p.title} ${p.description} ${p.url}`);
    const allMatch = tokens.every((t) => combined.includes(t));
    return allMatch ? { p, score: total } : null;
  })
    .filter((x): x is { p: PageEntry; score: number } => !!x)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map((x) => x.p);

  const now = Date.now();
  const scoredArticles = articles
    .filter((a) => new Date(a.publishedAt).getTime() <= now)
    .map((a) => {
      const combined = normalize(
        `${a.title} ${a.excerpt} ${a.tags.join(" ")} ${a.category} ${a.slug}`
      );
      const allMatch = tokens.every((t) => combined.includes(t));
      if (!allMatch) return null;
      const score =
        scoreMatch(a.title, tokens, 4) +
        scoreMatch(a.excerpt, tokens, 2) +
        scoreMatch(a.tags.join(" "), tokens, 1) +
        scoreMatch(a.category, tokens, 1);
      return { a, score };
    })
    .filter((x): x is { a: Article; score: number } => !!x)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.a);

  return { pages: scoredPages, articles: scoredArticles };
}
